import React, { useState, useEffect } from 'react';
import {
  updateUserAttributes, updatePassword,
  setUpTOTP, verifyTOTPSetup, updateMFAPreference, fetchMFAPreference,
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { User, Check, RefreshCw, AlertCircle, KeyRound, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';

export function MyAccount() {
  const { user, refreshUser } = useAuth();

  const [name,  setName]  = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone_number ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw,  setSavingPw]  = useState(false);
  const [pwMsg,     setPwMsg]     = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  // MFA state
  const [mfaEnabled,   setMfaEnabled]   = useState<boolean | null>(null); // null = still checking
  const [mfaSetupUri,  setMfaSetupUri]  = useState<string | null>(null);
  const [mfaSecret,    setMfaSecret]    = useState<string | null>(null);
  const [mfaVerifyCode,setMfaVerifyCode]= useState('');
  const [mfaLoading,   setMfaLoading]   = useState(false);
  const [mfaMsg,       setMfaMsg]       = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchMFAPreference()
      .then(pref => setMfaEnabled((pref.enabled ?? []).includes('TOTP')))
      .catch(() => setMfaEnabled(false));
  }, []);

  const startMfaSetup = async () => {
    setMfaMsg(null);
    setMfaLoading(true);
    try {
      const totpSetup = await setUpTOTP();
      const uri = totpSetup.getSetupUri('256 Metrix', user?.email ?? '');
      setMfaSetupUri(uri.toString());
      setMfaSecret(totpSetup.sharedSecret);
    } catch (e: any) {
      setMfaMsg({ type: 'error', text: e.message ?? 'Failed to start MFA setup' });
    } finally {
      setMfaLoading(false);
    }
  };

  const confirmMfaSetup = async () => {
    setMfaMsg(null);
    setMfaLoading(true);
    try {
      await verifyTOTPSetup({ code: mfaVerifyCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaEnabled(true);
      setMfaSetupUri(null);
      setMfaSecret(null);
      setMfaVerifyCode('');
      setMfaMsg({ type: 'ok', text: 'Two-factor authentication is now enabled.' });
    } catch (e: any) {
      setMfaMsg({ type: 'error', text: e.message ?? 'That code did not verify — try again.' });
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfa = async () => {
    if (!window.confirm('Turn off two-factor authentication for your account?')) return;
    setMfaMsg(null);
    setMfaLoading(true);
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaEnabled(false);
      setMfaMsg({ type: 'ok', text: 'Two-factor authentication has been turned off.' });
    } catch (e: any) {
      setMfaMsg({ type: 'error', text: e.message ?? 'Failed to disable MFA' });
    } finally {
      setMfaLoading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      await updateUserAttributes({
        userAttributes: {
          name,
          phone_number: phone || undefined,
        },
      });
      await refreshUser();
      setProfileMsg({ type: 'ok', text: 'Profile updated.' });
    } catch (e: any) {
      setProfileMsg({ type: 'error', text: e.message ?? 'Failed to update profile' });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    setPwMsg(null);
    if (newPw !== confirmPw) {
      setPwMsg({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (!currentPw || !newPw) {
      setPwMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    setSavingPw(true);
    try {
      await updatePassword({ oldPassword: currentPw, newPassword: newPw });
      setPwMsg({ type: 'ok', text: 'Password changed.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e: any) {
      setPwMsg({ type: 'error', text: e.message ?? 'Failed to change password' });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-slate-700 rounded-lg">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Account</h1>
            <p className="text-sm text-gray-500">Update your profile and password</p>
          </div>
        </div>

        {/* Profile */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
          <h2 className="text-sm font-bold text-gray-900">Profile</h2>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input
              type="text" value={user?.email ?? ''} disabled
              className="w-full border border-gray-200 bg-gray-100 text-gray-500 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Contact your administrator to change your email.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <input
              type="text" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+15551234567"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          {profileMsg && (
            <div className={`flex items-start space-x-2 rounded-lg px-3 py-2 ${profileMsg.type === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {profileMsg.type === 'ok'
                ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
              <p className={`text-xs ${profileMsg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{profileMsg.text}</p>
            </div>
          )}

          <button
            onClick={saveProfile}
            disabled={savingProfile || !name.trim()}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            <span>{savingProfile ? 'Saving…' : 'Save Profile'}</span>
          </button>
        </div>

        {/* Password */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
          <div className="flex items-center space-x-2">
            <KeyRound className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-bold text-gray-900">Change Password</h2>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
            <input
              type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
            <input
              type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
            <input
              type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>

          {pwMsg && (
            <div className={`flex items-start space-x-2 rounded-lg px-3 py-2 ${pwMsg.type === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {pwMsg.type === 'ok'
                ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
              <p className={`text-xs ${pwMsg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{pwMsg.text}</p>
            </div>
          )}

          <button
            onClick={changePassword}
            disabled={savingPw}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {savingPw ? <RefreshCw className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            <span>{savingPw ? 'Updating…' : 'Change Password'}</span>
          </button>
        </div>

        {/* Two-Factor Authentication */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
          <div className="flex items-center space-x-2">
            <Smartphone className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-bold text-gray-900">Two-Factor Authentication</h2>
          </div>

          {mfaEnabled === null ? (
            <p className="text-xs text-gray-400">Checking status…</p>
          ) : mfaEnabled ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <p className="text-xs">Two-factor authentication is enabled using an authenticator app.</p>
              </div>
              <button
                onClick={disableMfa}
                disabled={mfaLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                <span>Turn Off</span>
              </button>
            </div>
          ) : mfaSetupUri ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-600">Scan this with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code it shows.</p>
              <div className="flex justify-center bg-white p-4 border border-gray-200 rounded-lg">
                <QRCodeSVG value={mfaSetupUri} size={180} />
              </div>
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer">Can't scan? Enter this code manually</summary>
                <p className="mt-1 font-mono break-all">{mfaSecret}</p>
              </details>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">6-Digit Code</label>
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  value={mfaVerifyCode}
                  onChange={e => setMfaVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              <button
                onClick={confirmMfaSetup}
                disabled={mfaLoading || mfaVerifyCode.length !== 6}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>Verify & Enable</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Add an extra layer of security using an authenticator app.</p>
              <button
                onClick={startMfaSetup}
                disabled={mfaLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mfaLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                <span>Set Up Two-Factor Authentication</span>
              </button>
            </div>
          )}

          {mfaMsg && (
            <div className={`flex items-start space-x-2 rounded-lg px-3 py-2 ${mfaMsg.type === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {mfaMsg.type === 'ok'
                ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
              <p className={`text-xs ${mfaMsg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{mfaMsg.text}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
